import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, Popconfirm, message, Typography } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchPickupAddresses, createPickupAddress, updatePickupAddress, deletePickupAddress, syncPickupAddressesFromShipStation } from '../api/pickup_address.api';
import { fetchShipStationWarehouses } from '../api/products.api';

const COUNTRY_OPTIONS = [
  { label: 'Canada (CA)', value: 'CA' },
  { label: 'United States (US)', value: 'US' },
];

function formatAddress(r: any): string {
  const countryLabel = r.country === 'CA' ? 'Canada' : 'USA';
  return [r.address_line1, r.address_line2, r.city, `${r.province} ${r.postal_code}`, countryLabel]
    .filter(Boolean)
    .join(', ');
}

export default function PickupAddress() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const handleSyncFromSs = async (): Promise<void> => {
    setSyncing(true);
    try {
      const r = await syncPickupAddressesFromShipStation();
      message.success(`Synced from ShipStation — ${r.created} created, ${r.updated} updated${r.skipped > 0 ? `, ${r.skipped} skipped (incomplete)` : ''}.`);
      queryClient.invalidateQueries({ queryKey: ['pickup-addresses'] });
    } catch (err) {
      message.error(`Sync failed: ${(err as Error).message ?? 'unknown'}`);
    } finally {
      setSyncing(false);
    }
  };
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // React Query dedupes concurrent fetches + caches across remounts, so
  // React 19 StrictMode's double-invoke in dev collapses to a single call.
  const { data: addresses = [], isLoading: loading } = useQuery({
    queryKey: ['pickup-addresses'],
    queryFn: fetchPickupAddresses,
  });
  const { data: warehouses = [] } = useQuery<Array<{ inventory_warehouse_id: string; name: string }>>({
    queryKey: ['shipstation-warehouses'],
    queryFn: async () => {
      const rows = await fetchShipStationWarehouses();
      return Array.isArray(rows) ? rows : [];
    },
    staleTime: 5 * 60_000, // warehouses rarely change; skip refetch for 5 min
  });

  const load = () => queryClient.invalidateQueries({ queryKey: ['pickup-addresses'] });

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(record: any) {
    setEditing(record);
    setModalOpen(true);
  }

  // Modal has destroyOnHidden, so the Form is remounted every open. Populate
  // field values only after the modal (and Form) is actually mounted; calling
  // form.setFieldsValue before mount triggers antd's "not connected" warning.
  function handleAfterOpenChange(open: boolean) {
    if (!open) return;
    if (editing) form.setFieldsValue(editing);
  }

  async function handleSave() {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updatePickupAddress(editing.id, values);
        message.success('Address updated.');
      } else {
        await createPickupAddress(values);
        message.success('Address created.');
      }
      setModalOpen(false);
      load();
    } catch {
      message.error('Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    try {
      await deletePickupAddress(id);
      message.success('Address deleted.');
      load();
    } catch {
      message.error('Delete failed.');
    }
  }

  const columns = [
    { title: 'Name', dataIndex: 'name', width: 180 },
    {
      title: 'Formatted Address',
      render: (_: any, r: any) => (
        <Typography.Text copyable={{ text: formatAddress(r) }}>
          {formatAddress(r)}
        </Typography.Text>
      ),
    },
    { title: 'City', dataIndex: 'city', width: 120 },
    { title: 'Province', dataIndex: 'province', width: 90 },
    { title: 'Postal Code', dataIndex: 'postal_code', width: 110 },
    { title: 'Country', dataIndex: 'country', width: 80 },
    { title: 'UniUni Customer #', dataIndex: 'customer_no', width: 130 },
    { title: 'UniUni WH ID', dataIndex: 'uniuni_warehouse_id', width: 110, render: (v: number | null) => v ?? '-' },
    {
      title: 'Inventory WH',
      dataIndex: 'inventory_warehouse_id',
      width: 160,
      render: (id: string | null) =>
        id ? (warehouses.find((w) => w.inventory_warehouse_id === id)?.name ?? id) : '-',
    },
    {
      title: 'Actions',
      width: 130,
      render: (_: any, r: any) => (
        <Space>
          <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
          <Popconfirm title="Delete this address?" onConfirm={() => handleDelete(r.id)} okText="Yes" cancelText="No">
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Pickup Addresses</h2>
        <Space>
          <Button onClick={handleSyncFromSs} loading={syncing}>Sync from ShipStation</Button>
          <Button type="primary" onClick={openCreate}>Add Address</Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={addresses}
        columns={columns}
        pagination={false}
      />

      <Modal
        title={editing ? 'Edit Pickup Address' : 'Add Pickup Address'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={560}
        destroyOnHidden
        afterOpenChange={handleAfterOpenChange}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }} initialValues={{ country: 'CA' }}>
          <Form.Item name="name" label="Store / Warehouse Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. LA Warehouse" />
          </Form.Item>
          <Form.Item name="address_line1" label="Address Line 1" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. 14438 S Avalon Blvd" />
          </Form.Item>
          <Form.Item name="address_line2" label="Address Line 2">
            <Input placeholder="Suite, Unit, etc. (optional)" />
          </Form.Item>
          <Form.Item name="city" label="City" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Gardena" />
          </Form.Item>
          <Space style={{ width: '100%' }} size={12}>
            <Form.Item name="province" label="Province / State" rules={[{ required: true, message: 'Required' }]} style={{ flex: 1 }}>
              <Input placeholder="e.g. CA" />
            </Form.Item>
            <Form.Item name="postal_code" label="Postal Code" rules={[{ required: true, message: 'Required' }]} style={{ flex: 1 }}>
              <Input placeholder="e.g. 90248" />
            </Form.Item>
          </Space>
          <Form.Item name="country" label="Country" rules={[{ required: true, message: 'Required' }]}>
            <Select options={COUNTRY_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="customer_no"
            label="UniUni Customer #"
            tooltip="UniUni sub-account that owns this pickup contract. Leave blank to fall back to region defaults."
          >
            <Input placeholder="e.g. 3962" />
          </Form.Item>
          <Form.Item
            name="inventory_warehouse_id"
            label="ShipStation Inventory Warehouse"
            tooltip="Which ShipStation inventory warehouse to deduct stock from on shipment. Leave blank to skip inventory adjustment."
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Select warehouse (optional)"
              options={warehouses.map((w) => ({ label: w.name, value: w.inventory_warehouse_id }))}
            />
          </Form.Item>
          <Form.Item
            name="uniuni_warehouse_id"
            label="UniUni Warehouse ID"
            tooltip="Integer ID from UniUni docs 'Hub Warehouse Location' table (e.g. 1 = LAX). Required by /version2/orders/estimateshipping for zone/rate lookup."
          >
            <Input placeholder="e.g. 1" type="number" />
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 8 }}>
            Origin Contact
          </Typography.Title>
          <Form.Item name="company" label="Company">
            <Input placeholder="Company name" />
          </Form.Item>
          <Space style={{ display: 'flex' }}>
            <Form.Item name="phone" label="Phone" style={{ flex: 1 }}>
              <Input placeholder="+1 555 123 4567" />
            </Form.Item>
            <Form.Item name="email" label="Email" style={{ flex: 1 }}>
              <Input placeholder="ops@example.com" />
            </Form.Item>
          </Space>

          <Typography.Title level={5} style={{ marginTop: 24, marginBottom: 4 }}>
            Return Address (optional)
          </Typography.Title>
          <div style={{ color: '#666', fontSize: 12, marginBottom: 12 }}>
            Leave blank to use the pickup address as the return address on the label.
            Set these when the return-processing center is different from the pickup warehouse.
          </div>
          <Space style={{ display: 'flex' }}>
            <Form.Item name="return_name" label="Return Contact Name" style={{ flex: 1 }}>
              <Input placeholder="Return contact" />
            </Form.Item>
            <Form.Item name="return_company" label="Return Company" style={{ flex: 1 }}>
              <Input placeholder="Company" />
            </Form.Item>
          </Space>
          <Form.Item name="return_line1" label="Return Address Line 1">
            <Input placeholder="Street address" />
          </Form.Item>
          <Form.Item name="return_line2" label="Return Address Line 2">
            <Input placeholder="Suite, unit, etc." />
          </Form.Item>
          <Space style={{ display: 'flex' }}>
            <Form.Item name="return_city" label="Return City" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name="return_state" label="Return State/Province" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }}>
            <Form.Item name="return_postal_code" label="Return Postal Code" style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name="return_country" label="Return Country" style={{ flex: 1 }}>
              <Select allowClear options={COUNTRY_OPTIONS} />
            </Form.Item>
          </Space>
          <Form.Item name="return_phone" label="Return Phone">
            <Input placeholder="+1 555 123 4567" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
